// Use explicit VITE_API_BASE_URL if set, otherwise default to localhost for local dev
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || `http://localhost:3002`;

// Log the API URL being used (will be visible in browser console)
console.log("[API Config] Using API_BASE_URL:", API_BASE_URL);

// Profiles
export const PROFILE_BIO_MAX = 500;
export const PROFILE_DISPLAY_NAME_MAX = 100;
export const PROFILE_CONTEXT_MAX = 1500;
export const PROFILE_USERNAME_MIN = 3;
export const PROFILE_USERNAME_MAX = 25;

// Workspaces
export const WORKSPACE_NAME_MAX = 100;
export const WORKSPACE_DESCRIPTION_MAX = 500;
export const WORKSPACE_INSTRUCTIONS_MAX = 1500;

// Assistant
export const ASSISTANT_NAME_MAX = 100;
export const ASSISTANT_DESCRIPTION_MAX = 500;
export const ASSISTANT_PROMPT_MAX = 100000;

// Models to remove from the list
export const MODELS_TO_REMOVE = [
  "computer-use-preview-2025-03-11",
  "gpt-4o-mini-audio-preview-2024-12-17",
  "dall-e-3",
  "dall-e-2",
  "gpt-4o-audio-preview-2024-10-01",
  "gpt-4o-audio-preview",
  "gpt-4o-mini-realtime-preview-2024-12-17",
  "gpt-4o-mini-realtime-preview",
  "omni-moderation-latest",
  "gpt-4o-mini-audio-preview",
  "omni-moderation-2024-09-26",
  "gpt-4o-realtime-preview-2024-10-01",
  "babbage-002",
  "tts-1-hd-1106",
  "text-embedding-3-large",
  "gpt-4o-audio-preview-2024-12-17",
  "tts-1-hd",
  "gpt-4o-mini-search-preview",
  "tts-1-1106",
  "davinci-002",
  "gpt-3.5-turbo-1106",
  "gpt-4o-search-preview",
  "gpt-4o-mini-search-preview-2025-03-11",
  "gpt-4o-realtime-preview-2024-12-17",
  "gpt-4o-realtime-preview",
  "gpt-3.5-turbo-16k",
  "text-embedding-3-small",
  "text-embedding-ada-002",
  "gpt-4o-search-preview-2025-03-11",
  "tts-1",
  "computer-use-preview",
  "whisper-1",
  "gpt-3.5-turbo-0125",
];

// Premium models that require subscription
export const PREMIUM_MODELS = [
  // Empty array - no premium models anymore
];

// Free models
export const FREE_MODELS = [
  // OpenAI models
  "gpt-4.1",
  "gpt-4.1-mini",
  "gpt-4.1-nano",
  "gpt-4o",
  "gpt-4o-mini",
  "gpt-5",
  "gpt-5-mini",
  "gpt-5-nano",
  "gpt-5.1",
  "gpt-5.1-codex-mini",
  "gpt-5.1-codex",
  "gpt-5.2",

  // Anthropic models
  "claude-sonnet-4-20250514",
  "claude-sonnet-4-5-20250929",
  "claude-3-7-sonnet-20250219",
  "claude-3-5-haiku-20241022",

  // Gemini models
  "gemini-2.5-pro",
  "gemini-2.5-flash",
  "gemini-2.5-flash-lite",
  "gemini-3-pro",

  // Mistral models
  "mistral-small-latest",
  "open-mistral-nemo",

  // DeepSeek models
  "deepseek-chat",
  "deepseek-reasoner",

  // Groq models
  "llama-3.3-70b-versatile",
  "moonshotai/kimi-k2-instruct-0905",

  // X AI models (only free models)
  "grok-4-0709",
  "grok-3",
  "grok-code-fast-1",
  "grok-3-fast",
  "grok-3-mini",
  "grok-3-mini-fast",

  // Cohere models
  "command-a-03-2025",
  "command-r7b-12-2024",
  "command-r-plus-08-2024",
  "command-r-08-2024",

  // Perplexity models
  "sonar",
  "sonar-pro",
  // "sonar-deep-research",
];

// Model provider mappings
export const MODEL_PROVIDERS = {
  // X AI models
  "grok-4-0709": "xai",
  "grok-code-fast-1": "xai",
  "grok-3": "xai",
  "grok-3-latest": "xai",
  "grok-3-fast": "xai",
  "grok-3-fast-latest": "xai",
  "grok-3-mini": "xai",
  "grok-3-mini-latest": "xai",
  "grok-3-mini-fast": "xai",
  "grok-3-mini-fast-latest": "xai",
  "grok-2-vision-1212": "xai",
  "grok-2-vision": "xai",
  "grok-2-vision-latest": "xai",
  "grok-2-image-1212": "xai",
  "grok-2-image": "xai",
  "grok-2-image-latest": "xai",

  // DeepSeek models
  "deepseek-chat": "deepseek",
  "deepseek-coder": "deepseek",
  "deepseek-reasoner": "deepseek",

  // Gemini models
  "gemini-2.5-pro": "gemini",
  "gemini-2.5-flash": "gemini",
  "gemini-2.5-flash-lite": "gemini",
  "gemini-3-pro": "gemini",

  // Mistral models
  "mistral-small": "mistral",
  "mistral-small-latest": "mistral",
  "mistral-medium": "mistral",
  "mistral-large": "mistral",
  "mistral-large-latest": "mistral",
  "mistral-large-2-2407": "mistral",
  "mistral-nemo-latest": "mistral",
  "open-mistral-nemo": "mistral",

  // Anthropic models
  "claude-opus-4-20250514": "anthropic",
  "claude-sonnet-4-20250514": "anthropic",
  "claude-sonnet-4-5-20250929": "anthropic",
  "claude-3-7-sonnet-20250219": "anthropic",
  "claude-3-5-haiku-20241022": "anthropic",

  // Groq models
  "gemma2-9b": "groq",
  "llama-3.3-70b-versatile": "groq",
  "llama3-70b-8192": "groq",
  "llama3-8b-8192": "groq",
  "mixtral-8x7b-32768": "groq",
  "mistral-large-2402": "groq",
  "moonshotai/kimi-k2-instruct-0905": "groq",

  // Cohere models
  "command-a-03-2025": "cohere",
  "command-r7b-12-2024": "cohere",
  "command-r-plus-04-2024": "cohere",
  "command-r-plus-08-2024": "cohere",
  "command-r-08-2024": "cohere",
  "command-r-03-2024": "cohere",
  command: "cohere",
  "command-light": "cohere",

  // Perplexity models
  sonar: "perplexity",
  "sonar-pro": "perplexity",
  "sonar-deep-research": "perplexity",
  "sonar-reasoning": "perplexity",
  "sonar-reasoning-pro": "perplexity",
  "r1-1776": "perplexity",

  // OpenAI models default to 'openai' provider
  "gpt-4.1": "openai",
  "gpt-4.1-mini": "openai",
  "gpt-4.1-nano": "openai",
  "gpt-4o": "openai",
  "gpt-4o-mini": "openai",
  "gpt-5": "openai",
  "gpt-5-mini": "openai",
  "gpt-5-nano": "openai",
  "gpt-5.1": "openai",
  "gpt-5.1-codex": "openai",
  "gpt-5.1-codex-mini": "openai",
  "gpt-5.2": "openai",

  // OpenRouter uncensored models
  "cognitivecomputations/dolphin-mixtral-8x22b": "openrouter",
  "cognitivecomputations/dolphin-llama-3-70b": "openrouter",
  "cognitivecomputations/dolphin-2.6-mixtral-8x7b": "openrouter",
  "cognitivecomputations/dolphin-3.0-mistral-24b": "openrouter",
  "cognitivecomputations/dolphin-3.0-r1-mistral-24b": "openrouter",
  "nousresearch/nous-hermes-llama3-70b": "openrouter",
  "nousresearch/nous-hermes-2-mixtral-8x7b-sft": "openrouter",
  "nousresearch/nous-hermes-2-mistral-7b-dpo": "openrouter",
  "teknium/openhermes-2.5-mistral-7b": "openrouter",
  "migtissera/synthia-70b-v2": "openrouter",

  // Qwen models
  "qwen-max-latest": "qwen",
  "qwen-flash": "qwen",
  "qwen-turbo-latest": "qwen",
  "qwen3-235b-a22b-instruct": "qwen",
  "qwen3-235b-a22b-thinking-2507": "qwen",
  "qwen3-32b-instruct": "qwen",
};

// OpenAI Models
export const OPENAI_MODELS = {
  GPT_4_1: "gpt-4.1",
  GPT_4_1_MINI: "gpt-4.1-mini",
  GPT_4_1_NANO: "gpt-4.1-nano",
  GPT_4O: "gpt-4o",
  GPT_4O_MINI: "gpt-4o-mini",
  GPT_5: "gpt-5",
  GPT_5_MINI: "gpt-5-mini",
  GPT_5_NANO: "gpt-5-nano",
  GPT_5_1: "gpt-5.1",
  GPT_5_1_CODEX: "gpt-5.1-codex",
  GPT_5_1_CODEX_MINI: "gpt-5.1-codex-mini",
  GPT_5_2: "gpt-5.2",
};

// Google Models
export const GOOGLE_MODELS = {
  GEMINI_2_5_PRO: "gemini-2.5-pro",
  GEMINI_2_5_FLASH: "gemini-2.5-flash",
  GEMINI_2_5_FLASH_LITE: "gemini-2.5-flash-lite",
  GEMINI_3_PRO: "gemini-3-pro",
};

// Anthropic Models
export const ANTHROPIC_MODELS = {
  CLAUDE_OPUS_4: "claude-opus-4-20250514",
  CLAUDE_SONNET_4: "claude-sonnet-4-20250514",
  CLAUDE_SONNET_4_5: "claude-sonnet-4-5-20250929",
  CLAUDE_3_7_SONNET: "claude-3-7-sonnet-20250219",
  CLAUDE_3_5_HAIKU: "claude-3-5-haiku-20241022",
};

// Mistral Models
export const MISTRAL_MODELS = {
  TINY: "mistral-tiny",
  SMALL_LATEST: "mistral-small-latest",
  MEDIUM_LATEST: "mistral-medium-latest",
  LARGE_LATEST: "mistral-large-latest",
  OPEN_MISTRAL_NEMO: "open-mistral-nemo",
};

// Groq Models
export const GROQ_MODELS = {
  LLAMA3_8B: "llama3-8b-8192",
  LLAMA3_70B: "llama3-70b-8192",
  LLAMA3_3_70B_VERSATILE: "llama-3.3-70b-versatile",
  KIMI_K2_INSTRUCT: "moonshotai/kimi-k2-instruct-0905",
  MIXTRAL_8X7B: "mixtral-8x7b-32768",
  GEMMA_7B_IT: "gemma-7b-it",
};

// DeepSeek Models
export const DEEPSEEK_MODELS = {
  DEEPSEEK_CHAT: "deepseek-chat",
  DEEPSEEK_REASONER: "deepseek-reasoner",
  DEEPSEEK_CODER: "deepseek-coder",
};

// XAI Models
export const XAI_MODELS = {
  GROK_4_0709: "grok-4-0709",
  GROK_CODE_FAST_1: "grok-code-fast-1",
  GROK_3: "grok-3",
  GROK_3_FAST: "grok-3-fast",
  GROK_3_MINI: "grok-3-mini",
  GROK_3_MINI_FAST: "grok-3-mini-fast",
};

// Cohere Models
export const COHERE_MODELS = {
  COMMAND_A: "command-a-03-2025",
  COMMAND_R7B: "command-r7b-12-2024",
  COMMAND_R_PLUS: "command-r-plus-08-2024",
  COMMAND_R: "command-r-08-2024",
};

// Perplexity Models
export const PERPLEXITY_MODELS = {
  PPLX_7B_ONLINE: "pplx-7b-online",
  PPLX_70B_ONLINE: "pplx-70b-online",
  PPLX_7B_CHAT: "pplx-7b-chat",
  PPLX_70B_CHAT: "pplx-70b-chat",
  MIXTRAL_8X7B_INSTRUCT: "mixtral-8x7b-instruct",
  MISTRAL_7B_INSTRUCT: "mistral-7b-instruct",
  LLAMA_2_70B_CHAT: "llama-2-70b-chat",
  CODELLAMA_34B_INSTRUCT: "codellama-34b-instruct",
  CODELLAMA_70B_INSTRUCT: "codellama-70b-instruct",
  SONAR_SMALL_CHAT: "sonar-small-chat",
  SONAR_SMALL_ONLINE: "sonar-small-online",
  SONAR_MEDIUM_CHAT: "sonar-medium-chat",
  SONAR_MEDIUM_ONLINE: "sonar-medium-online",
  SONAR: "sonar",
  SONAR_PRO: "sonar-pro",
  SONAR_DEEP_RESEARCH: "sonar-deep-research",
};

// Qwen Models
export const QWEN_MODELS = {
  QWEN_MAX_LATEST: "qwen-max-latest",
  QWEN_FLASH: "qwen-flash",
  QWEN_TURBO_LATEST: "qwen-turbo-latest",
  QWEN3_235B_A22B_INSTRUCT: "qwen3-235b-a22b-instruct",
  QWEN3_235B_A22B_THINKING: "qwen3-235b-a22b-thinking-2507",
  QWEN3_32B_INSTRUCT: "qwen3-32b-instruct",
};

// OpenRouter Models
export const OPENROUTER_MODELS = {
  // No OpenRouter models currently enabled
};

// Combined array of all model IDs
export const ALL_MODEL_IDS = [
  ...Object.values(OPENAI_MODELS),
  ...Object.values(GOOGLE_MODELS),
  ...Object.values(ANTHROPIC_MODELS),
  ...Object.values(MISTRAL_MODELS),
  ...Object.values(GROQ_MODELS),
  ...Object.values(PERPLEXITY_MODELS),
  ...Object.values(DEEPSEEK_MODELS),
  ...Object.values(XAI_MODELS),
  ...Object.values(COHERE_MODELS),
  ...Object.values(QWEN_MODELS),
  ...Object.values(OPENROUTER_MODELS),
];

// Default enabled state for models in settings
// Models in FREE_MODELS are enabled by default (true)
// Non-free models can be specified here to show in settings but be disabled by default (false)
// Models not listed here will default to false (disabled) if they're not in FREE_MODELS
export const DEFAULT_MODEL_ENABLED_STATE = {
  // All FREE_MODELS are enabled by default
  ...Object.fromEntries(FREE_MODELS.map((model) => [model, true])),

  // Non-free xAI models are disabled by default but should appear in settings
  "grok-code-fast-1": false,
  "grok-3-fast": false,
  "grok-3-mini": false,

  // Models that should be disabled by default (overrides FREE_MODELS default)
  "gpt-4.1": false,
  "gpt-4o": false,
  "gpt-5-nano": false,
  "gpt-5.1-codex-mini": false,
  "gpt-5.1-codex": false,
  "command-a-03-2025": false,
  "command-r7b-12-2024": false,
  "command-r-plus-08-2024": false,
  "command-r-08-2024": false,
  "open-mistral-nemo": false,
  "mistral-small-latest": false,
  "claude-sonnet-4-20250514": false,
  "claude-3-7-sonnet-20250219": false,
  "claude-3-5-haiku-20241022": false,

  // Groq models - disabled by default
  "llama-3.3-70b-versatile": false,
  "moonshotai/kimi-k2-instruct-0905": false,

  // Qwen models - disabled by default
  "qwen-max-latest": false,
  "qwen-flash": false,
  "qwen-turbo-latest": false,
  "qwen3-235b-a22b-instruct": false,
  "qwen3-235b-a22b-thinking-2507": false,
  "qwen3-32b-instruct": false,
};

export const PROGRAMMING_LANGUAGES = [
  { language: "javascript", icon: "javascript" },
  { language: "typescript", icon: "typescript" },
  { language: "python", icon: "python" },
  { language: "java", icon: "java" },
  { language: "csharp", icon: "csharp" },
];

export default {
  OPENAI_MODELS,
  GOOGLE_MODELS,
  ANTHROPIC_MODELS,
  MISTRAL_MODELS,
  GROQ_MODELS,
  PERPLEXITY_MODELS,
  DEEPSEEK_MODELS,
  XAI_MODELS,
  COHERE_MODELS,
  QWEN_MODELS,
  OPENROUTER_MODELS,
  ALL_MODEL_IDS,
  PROGRAMMING_LANGUAGES,
  FREE_MODELS,
  PREMIUM_MODELS,
  MODEL_PROVIDERS,
  MODELS_TO_REMOVE,
  DEFAULT_MODEL_ENABLED_STATE,
};
